import type { SelectItem } from '@vrplatform/components';
import type { accountAssignmentType_enum } from '@vrplatform/graphql';
import { sortBy, toTitleCase } from '@vrplatform/utils';
import { formatAccountType, formatAssignmentType } from '@vrplatform/ui-common';
import type { Account } from '@vrplatform/data';

type AccountClassification = NonNullable<
  NonNullable<Account['category']>['classification']
>;

type ReservationLineTypeAccountOptionAccount = {
  id: string;
  name?: string | null;
  status?: 'active' | 'inactive' | null;
  type?: 'bank' | 'ledger' | 'recurringFee' | 'nonPosting' | null;
  category?: {
    name?: string | null;
    classification?: AccountClassification | null;
  } | null;
  assignments?: accountAssignmentType_enum[] | null;
};

const ACCOUNT_CLASSIFICATION_ORDER: Record<AccountClassification, number> = {
  asset: 0,
  liability: 1,
  revenue: 2,
  expense: 3,
};

const formatDisabledMessage = (assignmentType: accountAssignmentType_enum) =>
  `${formatAssignmentType(assignmentType)} account cannot be assigned to reservation lines to ensure the integrity of your accounting.`;

const getReservationLineTypeAccountSortKey = (
  account: ReservationLineTypeAccountOptionAccount
) => {
  const categoryOrder = account.category?.classification
    ? ACCOUNT_CLASSIFICATION_ORDER[account.category.classification]
    : Number.MAX_SAFE_INTEGER;
  const categoryName = (account.category?.name ?? '').toLowerCase();
  const name = (account.name ?? '-').toLowerCase();

  return `${String(categoryOrder).padStart(10, '0')}:${categoryName}:${name}`;
};

const getDisabledMessage = (
  assignments: accountAssignmentType_enum[]
): string | undefined => {
  if (assignments.includes('accountsReceivable')) {
    return formatDisabledMessage('accountsReceivable');
  }

  if (assignments.includes('accountsPayable')) {
    return formatDisabledMessage('accountsPayable');
  }

  if (assignments.includes('payout_distribution')) {
    return formatDisabledMessage('payout_distribution');
  }

  return undefined;
};

export const getReservationLineTypeAccountOptions = (
  accounts: ReservationLineTypeAccountOptionAccount[]
): SelectItem[] => {
  return sortBy(
    accounts.filter((account) => (account.status ?? 'active') === 'active'),
    getReservationLineTypeAccountSortKey
  ).map<SelectItem>((account) => {
    const assignments = (account.assignments ?? []).filter(
      (assignment): assignment is accountAssignmentType_enum => !!assignment
    );
    const disabled = getDisabledMessage(assignments);

    return {
      value: account.id,
      label: account.name || '-',
      group: toTitleCase(account.category?.classification || '-'),
      disabled,
      description:
        account.type === 'nonPosting' && !disabled
          ? `${formatAccountType('nonPosting')} account`
          : undefined,
    };
  });
};
